import React from 'react';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, InputNumber, Switch } from 'antd';
import type { AdminProductStockControlProps } from '../../interfaces';

type AdminProductVariantStockListProps = Pick<
  AdminProductStockControlProps,
  | 'material'
  | 'draftStockById'
  | 'isSavingStock'
  | 'requiresSizes'
  | 'productId'
  | 'onStockStep'
  | 'onDraftStockInput'
  | 'onCommitDraftStock'
  | 'onVariantAvailabilityChange'
>;

const AdminProductVariantStockList: React.FC<AdminProductVariantStockListProps> = ({
  material,
  draftStockById,
  isSavingStock,
  requiresSizes,
  productId,
  onStockStep,
  onDraftStockInput,
  onCommitDraftStock,
  onVariantAvailabilityChange,
}) => {
  const inventories = (material.variantInventories || []).filter((variant) =>
    !variant.product?.id || variant.product.id === productId
  );
  const variantRows = requiresSizes
    ? (material.sizes || []).map((size) => ({
      key: size.id || size.value,
      label: `${size.label} (${size.value})`,
      inventory: inventories.find((variant) => variant.size?.id === size.id),
      fallbackStock: material.stockQuantity,
      fallbackAvailable: material.isAvailable,
    }))
    : [{
      key: 'one-size',
      label: 'One size',
      inventory: inventories.find((variant) => !variant.size?.id) || inventories[0],
      fallbackStock: material.stockQuantity,
      fallbackAvailable: material.isAvailable,
    }];

  return (
    <div className="admin-product-form__variant-stock-list">
      <span className="admin-product-form__variant-stock-title">Variant Inventory</span>
      {variantRows.map((row) => {
        const inventoryId = row.inventory?.id || '';
        const stockQuantity = typeof row.inventory?.stockQuantity === 'number'
          ? row.inventory.stockQuantity
          : row.fallbackStock;
        const draftVariantStock = inventoryId
          ? draftStockById[inventoryId] ?? stockQuantity
          : stockQuantity;
        const isSavingVariant = inventoryId ? isSavingStock : false;

        return (
          <div key={row.key} className="admin-product-form__variant-stock-row">
            <span className="admin-product-form__variant-stock-label">{row.label}</span>
            <Switch
              size="small"
              checked={row.inventory?.isAvailable ?? row.fallbackAvailable}
              disabled={!inventoryId}
              onChange={(checked) => void onVariantAvailabilityChange(inventoryId, checked)}
            />
            <div className="admin-product-form__stock-stepper">
              <Button
                onClick={() => onStockStep(inventoryId, draftVariantStock, -1)}
                disabled={!inventoryId || isSavingVariant || draftVariantStock <= 0}
                icon={<MinusCircleOutlined />}
              />
              <InputNumber
                min={0}
                value={draftVariantStock}
                onChange={(value) => inventoryId && onDraftStockInput(inventoryId, value)}
                onBlur={() => inventoryId && onCommitDraftStock(inventoryId, stockQuantity)}
                disabled={!inventoryId || isSavingVariant}
                controls={false}
                className="admin-product-form__stock-input"
              />
              <Button
                onClick={() => onStockStep(inventoryId, draftVariantStock, 1)}
                disabled={!inventoryId || isSavingVariant}
                icon={<PlusOutlined />}
              />
            </div>
          </div>
        );
      })}
      {variantRows.length === 0 && (
        <span className="admin-product-form__variant-stock-empty">Select sizes to create inventory rows.</span>
      )}
    </div>
  );
};

export default AdminProductVariantStockList;
